import React, { useState } from "react";
import * as THREE from "three";
import { useSelector, useDispatch } from "react-redux";
import {
  updateWindowPosition,
  setTrue,
  toggleBoxForClickOutsideOfBox,
} from "../../redux/windowConfiguration";
import {
  useHandleWindowsSwitch,
  useGroupedWindowsData,
  useClickWrapper,
  useWindowPassFiltersCheck,
  useActiveElements,
} from "../CustomHooks";
import {
  getClickedWindowColor,
  getHoveredWindowColor,
  isWindowPreviouslySelected,
} from "../utils";
import { SimilarPolygonObjectsMesh } from "./InstancedMesh";
import useWindowSelection from "../objectSelection/useObjectSelection";
import { useCallback } from "react";
import { colors } from "../../utils/colors";
import { resources } from "../../redux/sensorAnalysisConstants";
import { isResourceInState } from "../../redux/sensorAnalysis";

const isWindowinFloor = (window, data, selectedFloor) => {
  for (const floorIndex in data) {
    const floor = data[floorIndex];
    if (selectedFloor[floorIndex])
      for (const windowIndex in floor) {
        const floorWindow = floor[windowIndex];
        if (window.id === floorWindow.id) {
          return true;
        }
      }
  }
  return false;
};

export const getDefaultWindowColor = () => {
  return colors.blue200();
};

function SimilarWindows({ windows, visible, data }) {
  const [hoveredWindow, setHoveredWindow] = useState(null);
  const dispatch = useDispatch();

  const activeElements = useActiveElements();
  const selectedFloor = useSelector(
    (state) => state.floorVisibility.selectedFloors
  );
  var clickedWindowId = useSelector(
    (state) => state.shadingConfigurationWindow.id
  );

  const sensoranalysisState = useSelector(
    (state) => state.sensorAnalysis.sensorAnalysisState
  );

  const manualMeshingIsOpen = isResourceInState(
    sensoranalysisState,
    resources.MANUAL_MESHING_ENABLED
  );

  const { handleObjectClick, selectedObjects } = useWindowSelection(windows);

  const isSelectionActive = isResourceInState(
    sensoranalysisState,
    resources.SELECTABLE_WINDOWS
  );

  const preRayConfigurations = useSelector(
    (state) => state.sensorAnalysis.preRay.configurations
  );

  const getSelectedWindowColor = () => {
    return colors.orange500();
  };

  const getPreviouslySelectedWindowColor = () => {
    return colors.unclickableObject();
  };

  const setColor = (object) => {
    const isSelected = selectedObjects?.includes(object?.id);

    let color;

    if (isWindowPreviouslySelected(object?.id, preRayConfigurations)) {
      color = getPreviouslySelectedWindowColor();
    } else if (hoveredWindow?.id === object?.id) {
      color = getHoveredWindowColor();
    } else if (isSelectionActive && isSelected) {
      color = getSelectedWindowColor();
    } else if (object?.id === clickedWindowId) {
      color = getClickedWindowColor();
    } else {
      color = getDefaultWindowColor();
    }

    return new THREE.Color(color);
  };

  const clickIsDisabled =
    !visible || manualMeshingIsOpen || !activeElements.windowClick;

  const onClick = (config) => {
    config.stopPropagation();
    if (clickIsDisabled) return;

    const window = windows[config.instanceId];
    if (isWindowPreviouslySelected(window?.id, preRayConfigurations)) return;
    dispatch(toggleBoxForClickOutsideOfBox());
    dispatch(setTrue());
    dispatch(updateWindowPosition([null, null, window.name, window.id]));
  };

  const onDoubleClick = (config) => {
    config.stopPropagation();
    if (clickIsDisabled) return;

    const window = windows[config.instanceId];
    if (isWindowPreviouslySelected(window?.id, preRayConfigurations)) return;
    handleObjectClick(window.id, config);
  };

  const { singleClickWrapper, doubleClickWrapper } = useClickWrapper();
  const singleClick = singleClickWrapper(onClick);
  const doubleClick = doubleClickWrapper(onDoubleClick);

  const { doesWindowPassFilters } = useWindowPassFiltersCheck();
  const isValid = useCallback(
    (object) => {
      if (!isWindowinFloor(object, data, selectedFloor)) return false;
      if (!doesWindowPassFilters(object?.id)) return false;

      return true;
    },
    [doesWindowPassFilters, selectedFloor, data]
  );

  const objects = windows?.map((window) => {
    try {
      const parsed = JSON.parse(window?.polygon);
      return { ...window, polygon: parsed };
    } catch {
      return window;
    }
  });

  const onPointerEnter = (config) => {
    if (
      !visible ||
      isResourceInState(sensoranalysisState, resources.MANUAL_MESHING_ENABLED)
    )
      return;
    const window = windows[config.instanceId];
    setHoveredWindow(window);
  };
  const onPointerLeave = () => {
    setHoveredWindow(null);
  };

  return (
    <SimilarPolygonObjectsMesh
      objects={objects}
      setColor={setColor}
      isValid={isValid}
      updateDependencyArray={[
        windows,
        selectedFloor,
        clickedWindowId,
        isSelectionActive,
        selectedObjects,
        hoveredWindow,
        isValid,
      ]}
      groupProps={{
        visible,
        onClick: (config) => {
          config.stopPropagation();
          singleClick(config);
        },
        onPointerEnter,
        onPointerLeave,
        onDoubleClick: (config) => {
          config.stopPropagation();
          doubleClick(config);
        },
      }}
    />
  );
}

const Window = () => {
  const { groupedWindows, resultArray } = useGroupedWindowsData();

  useHandleWindowsSwitch();

  const randomNumberForForceRecreate = Math.random();
  var clickedWindowId = useSelector(
    (state) => state.shadingConfigurationWindow.id
  );

  var targetBuildingScene = useSelector(
    (state) => state.sceneVisibility.value[4]
  );
  if (!resultArray?.length > 0) return;
  return (
    <>
      {groupedWindows.map((group, index) => {
        return (
          <SimilarWindows
            key={index + clickedWindowId + randomNumberForForceRecreate}
            windows={group}
            visible={targetBuildingScene}
            data={resultArray}
          />
        );
      })}
    </>
  );
};

export default React.memo(Window);
